/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnChanges, OnInit,
  Output, SimpleChanges, ViewChild, signal,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CommentService } from '../../../../services/comment.service';
import { KeysetFeedStoreService } from '../../../../services/keyset-feed-store.service';
import { EMPTY_CONTENT_MARKDOWNS } from '../../../../../constants';
import { UtEditorComponent } from '../../../ut-editor/ut-editor.component';

// Older comments load a few at a time.
const COMMENTS_PER_PAGE = 5;

interface RenderedComment {
  id: number;
  user: any;
  published_at: string;
  html: SafeHtml;
}

// Comment form and recent comments under a community feed card.
@Component({
  selector: 'ut-community-feed-item-comments',
  templateUrl: './community-feed-item-comments.component.html',
  styleUrls: ['./community-feed-item-comments.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CommunityFeedItemCommentsComponent implements OnInit, OnChanges {
  @ViewChild(UtEditorComponent) editor?: UtEditorComponent;
  @Input({ required: true }) item: any;
  @Input({ required: true }) user: any;
  // Opened from the card's "Show more comments": load the next few comments straight away.
  @Input() loadMoreOnOpen = false;
  // The write-a-comment form only shows after "Add comment"; "Show more comments" just reads.
  @Input() showForm = true;
  @Output() commented = new EventEmitter<void>();

  newComment = { content: '' };
  posting = signal(false);
  announcement = signal('');
  recentComments: RenderedComment[] = [];
  olderComments: RenderedComment[] = [];
  commentFeed: any;

  constructor(
    @Inject('$filter') private $filter: any,
    @Inject('AlertService') private AlertService: any,
    private keysetFeedStore: KeysetFeedStoreService,
    private commentService: CommentService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.reset(this.item);
    if (this.loadMoreOnOpen && this.canLoadMore) {
      this.loadMore();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // A hidden form must not keep its draft for the next card that opens it.
    if (changes.showForm && !this.showForm) {
      this.newComment = { content: '' };
    }
  }

  get canLoadMore(): boolean {
    const total = this.item.comments ? this.item.comments.count : this.item.post_count;
    return total > this.recentComments.length && !this.commentFeed.isLoading
      && !(this.commentFeed.isLastPage && this.commentFeed.feedItems.length > 0);
  }

  submit(): void {
    if (this.posting()) return;
    const content = (this.newComment.content || '').trim();
    if (!content || EMPTY_CONTENT_MARKDOWNS.includes(content)) {
      this.AlertService.addDanger('Can\'t post empty comment!');
      return;
    }
    this.posting.set(true);
    this.commentService.postCommentAndReturnCommentable(this.item.type, this.item.id, this.newComment).then((res) => {
      if (res.data.comments) {
        this.item.comments.count = res.data.comments.count;
      } else {
        this.item.post_count = res.data.post_count;
      }
      this.item.likes.count = res.data.likes.count;
      this.item.recent_comments = res.data.recent_comments;
      this.reset(res.data);
      this.newComment = { content: '' };
      this.editor?.exitPreview();
      this.announcement.set('Comment posted');
      this.commented.emit();
    }, () => {
      this.AlertService.addDanger('Your comment couldn\'t be posted. Please try again.');
    }).finally(() => {
      this.posting.set(false);
      this.cdr.markForCheck();
    });
  }

  loadMore(): void {
    const recent = this.item.recent_comments || [];
    this.commentFeed.loadMore(recent[recent.length - 1]?.id).then((store) => {
      // A topic's oldest "comment" is its opening post, which the card already shows.
      if (this.item.type === 'topic' && store.isLastPage) {
        store.feedItems.pop();
      }
      this.olderComments = store.feedItems.map((c) => this.render(c));
      this.cdr.markForCheck();
    });
    this.cdr.markForCheck();
  }

  private reset(commentable: any): void {
    this.recentComments = (commentable.recent_comments || []).map((c) => this.render(c));
    this.olderComments = [];
    this.commentFeed = this.keysetFeedStore.create(
      (params) => this.commentService.feed(params),
      { id: commentable.id, type: commentable.type, per_page: COMMENTS_PER_PAGE },
    );
  }

  private render(comment: any): RenderedComment {
    return {
      id: comment.id,
      user: comment.user,
      published_at: comment.published_at,
      html: this.sanitizer.bypassSecurityTrustHtml(this.$filter('uMarkdown')(comment.content)),
    };
  }
}
